import { AffineOtherPageLayout } from '@affine/component/affine-other-page-layout';
import { SignInPageContainer } from '@affine/component/auth-components';
import { SignInPanel } from '@affine/core/components/sign-in';
import { SignInBackgroundArts } from '@affine/core/components/sign-in/background-arts';
import type { AuthSessionStatus } from '@affine/core/modules/cloud/entities/session';
import { useCallback } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import {
  RouteLogic,
  useNavigateHelper,
} from '../../../components/hooks/use-navigate-helper';
import { OAuthErrorPage } from './oauth-error';

export const SignIn = ({
  redirectUrl: redirectUrlFromProps,
}: {
  redirectUrl?: string;
}) => {
  const navigate = useNavigate();
  const { jumpToIndex } = useNavigateHelper();
  const [searchParams] = useSearchParams();
  const redirectUrl = redirectUrlFromProps ?? searchParams.get('redirect_uri');

  const server = searchParams.get('server') ?? undefined;
  const error = searchParams.get('error');
  const oauthParams = new URLSearchParams({
    provider: 'FydeOS',
    client: 'web',
    flow: 'redirect',
  });
  if (redirectUrl) {
    oauthParams.set('redirect_uri', redirectUrl);
  }
  const oauthLoginPath = `/oauth/login?${oauthParams.toString()}`;

  const handleClose = useCallback(() => {
    const backSearchParams = new URLSearchParams(searchParams);
    backSearchParams.delete('error');
    jumpToIndex(RouteLogic.REPLACE, {
      search: backSearchParams.toString(),
    });
  }, [jumpToIndex, searchParams]);

  const handleRetryOAuth = useCallback(() => {
    Promise.resolve(navigate(oauthLoginPath, { replace: true })).catch(
      console.error
    );
  }, [navigate, oauthLoginPath]);

  const handleAuthenticated = useCallback(
    (status: AuthSessionStatus) => {
      if (status === 'authenticated') {
        if (redirectUrl) {
          if (redirectUrl.toUpperCase() === 'CLOSE_POPUP') {
            window.close();
          }
          Promise.resolve(
            navigate(redirectUrl, {
              replace: true,
            })
          ).catch(console.error);
        } else {
          handleClose();
        }
      }
    },
    [handleClose, navigate, redirectUrl]
  );

  const initStep = server ? 'addSelfhosted' : 'signIn';

  if (error) {
    return <OAuthErrorPage onRetry={handleRetryOAuth} onBack={handleClose} />;
  }

  if (BUILD_CONFIG.isWeb) {
    return <Navigate to={oauthLoginPath} replace />;
  }

  return (
    <SignInPageContainer>
      <div style={{ maxWidth: '400px', width: '100%', zIndex: 1 }}>
        <SignInPanel
          onSkip={handleClose}
          onAuthenticated={handleAuthenticated}
          initStep={initStep}
          server={server}
          redirectUrl={redirectUrl ?? undefined}
        />
      </div>
    </SignInPageContainer>
  );
};

export const Component = () => {
  const [searchParams] = useSearchParams();

  return (
    <AffineOtherPageLayout>
      {!searchParams.has('error') && <SignInBackgroundArts />}
      <SignIn />
    </AffineOtherPageLayout>
  );
};
